import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import WeeklyPromoters from "@/components/WeeklyPromoters";
import { Cpu, Trophy, Calendar, ArrowLeft, Sparkles, Flame, AlertTriangle, ExternalLink } from "lucide-react";
import ToniniVisionCTA from "@/components/ToniniVisionCTA";

export default function Cycles() {
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, h, cfg] = await Promise.all([
          api.get("/cycles/current"),
          api.get("/cycles/history?limit=50"),
          api.get("/payments/crypto/config"),
        ]);
        setCurrent(c.data);
        setHistory(h.data.cycles);
        setConfig(cfg.data);
      } catch {}
    })();
  }, []);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="cycles-page">
      <div className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 circuit-bg opacity-20" />
        <div className="absolute inset-0 bg-black/85" />
        <div className="absolute inset-0 nasa-grid-bg opacity-50" />

        <div className="relative max-w-5xl mx-auto px-4 md:px-8 py-10">
          <div className="flex items-start justify-between mb-6">
            <Logo size={48} withText />
            <Link to="/dashboard" className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-[#22ff77]">
              <ArrowLeft className="w-3 h-3" /> Nazad
            </Link>
          </div>
          <p className="text-xs uppercase tracking-[0.4em] text-zinc-500">Neprekidni Giveaway</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-[0.05em] mt-2">
            <span className="text-neon-orange">Ciklusi</span> mašina
          </h1>
          <p className="text-sm text-zinc-400 mt-3 max-w-2xl">
            Svaki ciklus prikuplja <span className="text-[#22ff77] font-bold">5.000 €</span> za kupovinu jedne mašine.
            Kada se cilj dostigne, sistem automatski izvlači pobednika i odmah otvara naredni ciklus.
          </p>

          {current && current.tickets_to_close > 0 && (current.urgency === "critical" || current.urgency === "high") && (
            <div
              data-testid="cycles-hero-fomo"
              className="mt-6 border-2 rounded-sm p-5 md:p-6 relative overflow-hidden"
              style={{
                borderColor: current.urgency === "critical" ? "#ff3b3b" : "#ff7a18",
                background: current.urgency === "critical" ? "rgba(255,59,59,0.06)" : "rgba(255,122,24,0.06)",
                boxShadow: current.urgency === "critical"
                  ? "0 0 0 1px #ff3b3b40, 0 0 50px #ff3b3b30"
                  : "0 0 0 1px #ff7a1840, 0 0 40px #ff7a1825",
              }}
            >
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  {current.urgency === "critical" ? (
                    <AlertTriangle className="w-12 h-12 text-[#ff3b3b] animate-pulse" strokeWidth={1.5} />
                  ) : (
                    <Flame className="w-12 h-12 text-[#ff7a18] animate-pulse" strokeWidth={1.5} />
                  )}
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.4em]" style={{ color: current.urgency === "critical" ? "#ff3b3b" : "#ff7a18" }}>
                      Ciklus se zatvara
                    </div>
                    <div className="font-display text-3xl md:text-5xl font-bold leading-none mt-1">
                      Još <span style={{ color: current.urgency === "critical" ? "#ff3b3b" : "#ff7a18" }} data-testid="hero-tickets-left">{current.tickets_to_close}</span> tiketa
                    </div>
                    <div className="text-sm text-zinc-300 mt-1">
                      <span className="text-white font-mono">{current.remaining_eur.toLocaleString("sr-Latn-RS")} €</span> do izvlačenja Mašine #{current.machine_number}
                    </div>
                  </div>
                </div>
                <Link
                  to="/kupi-tikete"
                  data-testid="hero-buy-cta"
                  className="bg-[#22ff77] text-black font-bold px-6 py-4 uppercase tracking-[0.2em] text-sm rounded-sm hover:brightness-110 glow-green whitespace-nowrap"
                >
                  Kupi tiket sada
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-10 space-y-10">
        <WeeklyPromoters />

        {current && (
          <section data-testid="cycles-current-section">
            <div className="flex items-center gap-2 mb-4">
              <Cpu className="w-5 h-5 text-[#22ff77]" />
              <h2 className="font-display text-2xl font-bold">Aktivni ciklus</h2>
            </div>
            <div className="nasa-card p-6 md:p-8 relative overflow-hidden">
              <div className="absolute inset-0 nasa-grid-bg opacity-40" />
              <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.4em] text-zinc-500">Mašina</div>
                  <div className="font-display text-7xl font-bold text-neon-orange mt-1">#{current.machine_number}</div>
                  <div className="text-xs text-zinc-500 mt-2 font-mono">
                    Otvoreno: {new Date(current.started_at).toLocaleString("sr-Latn-RS")}
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between text-[10px] uppercase tracking-widest">
                    <span className="text-zinc-500">Napredak</span>
                    <span className="text-[#22ff77] font-bold">{current.progress_pct}%</span>
                  </div>
                  {(() => {
                    const wallet = config?.wallet_address;
                    const isPlaceholder = !wallet || config?.placeholder_address;
                    const tronscanUrl = !isPlaceholder ? `https://tronscan.org/#/address/${wallet}` : null;
                    const barInner = (
                      <div
                        className={`h-full transition-all ${current.urgency === "critical" ? "animate-pulse" : ""}`}
                        style={{
                          width: `${current.progress_pct}%`,
                          background:
                            current.urgency === "critical"
                              ? "linear-gradient(90deg, #ff7a18, #ff3b3b)"
                              : "linear-gradient(90deg, #22ff77, #ff7a18)",
                          boxShadow:
                            current.urgency === "critical"
                              ? "0 0 14px rgba(255,59,59,0.6)"
                              : "0 0 14px rgba(34,255,119,0.5)",
                        }}
                      />
                    );
                    return tronscanUrl ? (
                      <a
                        href={tronscanUrl}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="public-progress-tronscan"
                        title="Otvori wallet na Tronscan exploreru"
                        className="block h-3 bg-black border border-white/10 rounded-sm overflow-hidden hover:border-[#22ff77]/40 cursor-pointer"
                      >
                        {barInner}
                      </a>
                    ) : (
                      <div className="h-3 bg-black border border-white/10 rounded-sm overflow-hidden">{barInner}</div>
                    );
                  })()}
                  <div className="grid grid-cols-3 gap-3">
                    {(() => {
                      const wallet = config?.wallet_address;
                      const isPlaceholder = !wallet || config?.placeholder_address;
                      const tronscanUrl = !isPlaceholder ? `https://tronscan.org/#/address/${wallet}` : null;
                      const value = `€${current.collected_eur.toLocaleString("sr-Latn-RS")}`;
                      return tronscanUrl ? (
                        <div className="border border-white/10 bg-black/40 p-3 rounded-sm">
                          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Sakupljeno</div>
                          <a
                            href={tronscanUrl}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="public-collected-tronscan"
                            className="font-display text-lg font-bold mt-1 inline-flex items-center gap-1 text-[#22ff77] hover:underline"
                          >
                            {value} <ExternalLink className="w-3 h-3 opacity-70" />
                          </a>
                          <div className="text-[9px] text-zinc-600 mt-1">klikni za Tronscan</div>
                        </div>
                      ) : (
                        <Mini label="Sakupljeno" value={value} color="#22ff77" subtitle="samo realne kupovine" />
                      );
                    })()}
                    <Mini label="Cilj" value={`€${current.target_eur.toLocaleString("sr-Latn-RS")}`} color="#ff7a18" />
                    <Mini label="Tiketi" value={current.tickets_sold} color="#fff" />
                  </div>
                  <p className="text-[10px] text-zinc-500 leading-relaxed pt-1 border-t border-white/5" data-testid="public-usdt-disclaimer">
                    <span className="text-[#22ff77]">●</span>{" "}
                    Prikaz u Evrima zbog lakšeg razumevanja. <span className="text-zinc-400">Sva sredstva se prikupljaju i obračunavaju u USDT-u na TRC20 mreži. 1 EUR ≈ 1 USDT.</span>
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        <section data-testid="cycles-history-section">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-5 h-5 text-[#ff7a18]" />
            <h2 className="font-display text-2xl font-bold">Završeni ciklusi</h2>
            <span className="text-xs text-zinc-500">({history.length})</span>
          </div>

          {history.length === 0 ? (
            <div className="nasa-card p-10 text-center text-zinc-500 text-sm">
              Još nijedan ciklus nije zatvoren. Budite među prvima — kupite tikete!
              <div className="mt-4">
                <Link to="/kupi-tikete" className="inline-flex items-center gap-2 text-[#22ff77] hover:underline">
                  <Sparkles className="w-3 h-3" /> Kupi tiket
                </Link>
              </div>
            </div>
          ) : (
            <div className="nasa-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 border-b border-white/5">
                      <th className="text-left px-6 py-4">Mašina</th>
                      <th className="text-left px-6 py-4">Pobednik</th>
                      <th className="text-right px-6 py-4">Sakupljeno</th>
                      <th className="text-right px-6 py-4">Tiketi</th>
                      <th className="text-left px-6 py-4">Datum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((c, i) => (
                      <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`cycle-row-${i}`}>
                        <td className="px-6 py-4">
                          <span className="font-display text-2xl font-bold text-[#ff7a18]">#{c.machine_number}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-semibold">{c.winner_name || "—"}</div>
                          <div className="text-[10px] text-zinc-500 font-mono">{c.winner_email_masked || ""}</div>
                        </td>
                        <td className="px-6 py-4 text-right font-mono text-[#22ff77]">{c.collected_eur.toLocaleString("sr-Latn-RS")} €</td>
                        <td className="px-6 py-4 text-right">{c.tickets_sold}</td>
                        <td className="px-6 py-4 text-zinc-400 text-xs font-mono">
                          <Calendar className="inline w-3 h-3 mr-1 text-zinc-600" />
                          {c.closed_at ? new Date(c.closed_at).toLocaleString("sr-Latn-RS") : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>

        <ToniniVisionCTA />
      </div>
    </div>
  );
}

function Mini({ label, value, color, subtitle }) {
  return (
    <div className="border border-white/10 bg-black/40 p-3 rounded-sm">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-display text-lg font-bold mt-1" style={{ color }}>{value}</div>
      {subtitle && <div className="text-[9px] text-zinc-600 mt-1 leading-tight">{subtitle}</div>}
    </div>
  );
}
